/**
 * GEX by Strike bar chart
 * Green bars = positive GEX (dealers long gamma — suppresses moves)
 * Red bars   = negative GEX (dealers short gamma — amplifies moves)
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell } from 'recharts';
import { Box, Typography, Chip } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  gexByStrike: Record<string, number>;
  gexFlipPrice?: number;
  spotPrice?: number;
  gexTotal?: number;
}

const GEXChart: React.FC<Props> = ({ gexByStrike, gexFlipPrice, spotPrice, gexTotal }) => {
  const { C } = useAppTheme();

  if (!gexByStrike) return null;

  const chartData = Object.entries(gexByStrike)
    .map(([strike, gex]) => ({ strike: Number(strike), gex: gex / 1e6 }))
    .sort((a, b) => a.strike - b.strike);

  if (chartData.length === 0) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>GEX by Strike</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {gexTotal !== undefined && (
            <Chip
              size="small"
              label={`Total: ${(gexTotal / 1e6).toFixed(1)}M`}
              sx={{
                bgcolor: gexTotal >= 0 ? C.bullMuted : C.bearMuted,
                color:   gexTotal >= 0 ? C.bull : C.bear,
                fontSize: '0.7rem', fontWeight: 700
              }}
            />
          )}
          {gexFlipPrice && (
            <Chip size="small" label={`Flip: ₹${gexFlipPrice}`}
              sx={{ bgcolor: C.accentMuted, color: C.accent, fontSize: '0.7rem', fontWeight: 700 }} />
          )}
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
        Green = dealers hedge against moves (suppresses). Red = dealers amplify moves.
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="strike" angle={-45} textAnchor="end" tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={v => `${v.toFixed(1)}M`} tick={{ fontSize: 10 }} />
          <Tooltip
            formatter={(v: any) => [`${Number(v).toFixed(2)}M`, 'GEX']}
            labelFormatter={l => `Strike: ${l}`}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
          />
          {spotPrice && <ReferenceLine x={spotPrice} stroke={C.spot} strokeWidth={2} />}
          {gexFlipPrice && <ReferenceLine x={gexFlipPrice} stroke={C.accent} strokeDasharray="4 4" />}
          <Bar dataKey="gex" maxBarSize={16} radius={[2, 2, 0, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.gex >= 0 ? C.chartBull : C.chartBear} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default GEXChart;
